/** UXP-00001 — Enterprise Identity Platform framework (mirrors pkg/identityxp) */

export const UXP_IX_ID = 'UXP-00001';

export interface PlatformRoleSpec {
  role: string;
  label: string;
  description?: string;
  default_home?: string;
}

export interface OnboardingStepSpec {
  step: string;
  label: string;
  order: number;
  description?: string;
}

export interface SyncDomainSpec {
  domain: string;
  label: string;
  default_selected: boolean;
  priority?: string;
}

export interface FunnelStage {
  funnel: string;
  stage: string;
  order: number;
}

export const AUTH_METHODS = [
  { id: 'email_password', label: 'Email/Password', status: 'live' as const },
  { id: 'google_oauth', label: 'Google OAuth', status: 'configurable' as const },
  { id: 'microsoft_oauth', label: 'Microsoft OAuth', status: 'configurable' as const },
  { id: 'remember_me', label: 'Remember Me', status: 'live' as const },
  { id: 'session_persistence', label: 'Session Persistence', status: 'live' as const },
  { id: 'device_recognition', label: 'Device Recognition', status: 'planned' as const },
  { id: 'enterprise_sso', label: 'Enterprise SSO', status: 'future' as const },
  { id: 'saml', label: 'SAML', status: 'future' as const },
  { id: 'oidc', label: 'OIDC', status: 'future' as const },
];

export const REGISTRATION_FEATURES = [
  'Email Verification', 'Password Strength', 'Terms Acceptance', 'Captcha',
  'Referral Codes', 'Invite Codes', 'Marketing Consent', 'Duplicate Detection',
];

export const ORG_SETUP_FIELDS = [
  'Organization Name', 'Workspace Name', 'Brand Context', 'Timezone',
  'Language', 'Currency', 'Business Profile', 'Default Preferences',
];

export const USER_MGMT_CAPABILITIES = [
  'Profile', 'Roles', 'Departments', 'Permissions',
  'Invitations', 'Activation', 'Suspension', 'Audit',
];

export const PLATFORM_ROLES: PlatformRoleSpec[] = [
  { role: 'organization_owner', label: 'Organization Owner', description: 'Full organization control', default_home: '/command' },
  { role: 'administrator', label: 'Administrator', description: 'Platform administration', default_home: '/admin' },
  { role: 'brand_owner', label: 'Brand Owner', description: 'Brand and workspace management', default_home: '/command' },
  { role: 'performance_marketing', label: 'Performance Marketing', description: 'Advertising and campaigns', default_home: '/amazon/advertising' },
  { role: 'listing', label: 'Listing', description: 'Catalog and content operations', default_home: '/amazon/catalog-ops' },
  { role: 'operations_fba', label: 'Operations FBA', description: 'Inventory, orders, FBA', default_home: '/amazon/fba-ops' },
  { role: 'finance', label: 'Finance', description: 'Settlements and finance ops', default_home: '/amazon/finance-ops' },
  { role: 'support', label: 'Support', description: 'Customer and post-sales', default_home: '/amazon/post-sales-ops' },
  { role: 'read_only', label: 'Read Only', description: 'View-only access', default_home: '/dashboard' },
  { role: 'custom_roles', label: 'Custom Roles', description: 'Tenant-defined roles', default_home: '/dashboard' },
];

export const SECURITY_CONTROLS = [
  'MFA', 'Password Policies', 'Brute Force Protection', 'Rate Limiting',
  'Session Management', 'Device Management', 'IP Monitoring', 'Geo Anomaly Detection', 'Audit Logging',
];

export const ACTIVATION_FEATURES = [
  'Free Trial', 'Paid Plans', 'Credits', 'Admin Approval', 'Manual Activation',
  'Coupon Codes', 'Feature Flags', 'Marketplace Limits', 'Amazon Connection Rules',
];

export const AMAZON_ONBOARDING_STEPS = [
  'OAuth Authorization', 'Marketplace Selection', 'Seller Authorization',
  'Connection Health', 'Token Refresh', 'Permission Validation', 'Initial Synchronization',
];

export const SYNC_DOMAINS: SyncDomainSpec[] = [
  { domain: 'catalog', label: 'Catalog', default_selected: true, priority: 'high' },
  { domain: 'brands', label: 'Brands', default_selected: true, priority: 'high' },
  { domain: 'orders', label: 'Orders', default_selected: true, priority: 'high' },
  { domain: 'inventory', label: 'Inventory', default_selected: true, priority: 'high' },
  { domain: 'advertising', label: 'Advertising', default_selected: false, priority: 'medium' },
  { domain: 'business_reports', label: 'Business Reports', default_selected: false, priority: 'medium' },
  { domain: 'brand_analytics', label: 'Brand Analytics', default_selected: false, priority: 'low' },
  { domain: 'settlements', label: 'Settlements', default_selected: false, priority: 'medium' },
];

export const AI_ONBOARDING = [
  'Guided Tour', 'Role-specific Introduction', 'AI Copilot Walkthrough',
  'First Recommendation', 'First Success Milestone', 'Goal Selection', 'Personalized Workspace',
];

export const PERSONALIZATION_OPTIONS = [
  'Business Goals', 'Revenue Targets', 'AI Preferences', 'Dashboard Layout',
  'Notifications', 'Workspace Defaults', 'Theme', 'Language', 'Timezone',
];

export const NOTIFICATION_CHANNELS = [
  'Email', 'Browser', 'Digest', 'Critical Alerts', 'Marketplace Events', 'Approval Requests',
];

export const ONBOARDING_STEPS: OnboardingStepSpec[] = [
  { step: 'welcome', label: 'Welcome', order: 0, description: 'Introduction to AI-COS' },
  { step: 'verify_email', label: 'Verify Email', order: 1, description: 'Confirm email address' },
  { step: 'organization', label: 'Organization', order: 2, description: 'Create organization' },
  { step: 'brand', label: 'Brand', order: 3, description: 'Define brand context' },
  { step: 'workspace', label: 'Workspace', order: 4, description: 'Configure workspace' },
  { step: 'amazon_oauth', label: 'Amazon OAuth', order: 5, description: 'Connect Amazon seller account' },
  { step: 'initial_sync', label: 'Initial Sync', order: 6, description: 'Synchronize catalog, orders, inventory' },
  { step: 'brand_dna', label: 'Brand DNA', order: 7, description: 'Generate structured brand intelligence' },
  { step: 'dashboard', label: 'Dashboard', order: 8, description: 'Enter personalized workspace' },
];

export const LANDING_PAGES = [
  'Public Home', 'Pricing', 'Features', 'Customer Stories', 'FAQ', 'Documentation',
  'Release Notes', 'System Status', 'Support', 'Contact', 'Privacy', 'Terms', 'Cookies',
];

export const ANALYTICS_FUNNELS: FunnelStage[] = [
  { funnel: 'registration', stage: 'Visit', order: 1 },
  { funnel: 'registration', stage: 'Register', order: 2 },
  { funnel: 'registration', stage: 'Verify', order: 3 },
  { funnel: 'registration', stage: 'Active', order: 4 },
  { funnel: 'activation', stage: 'Active User', order: 1 },
  { funnel: 'activation', stage: 'Org Created', order: 2 },
  { funnel: 'activation', stage: 'Plan Selected', order: 3 },
  { funnel: 'amazon_connection', stage: 'Org Ready', order: 1 },
  { funnel: 'amazon_connection', stage: 'OAuth Started', order: 2 },
  { funnel: 'amazon_connection', stage: 'Connected', order: 3 },
  { funnel: 'amazon_connection', stage: 'Healthy', order: 4 },
  { funnel: 'ai_adoption', stage: 'Connected', order: 1 },
  { funnel: 'ai_adoption', stage: 'First Copilot', order: 2 },
  { funnel: 'ai_adoption', stage: 'First Recommendation', order: 3 },
  { funnel: 'onboarding_completion', stage: 'Started', order: 1 },
  { funnel: 'onboarding_completion', stage: 'Sync Complete', order: 2 },
  { funnel: 'onboarding_completion', stage: 'DNA Generated', order: 3 },
  { funnel: 'onboarding_completion', stage: 'Dashboard', order: 4 },
];

export interface IdentityDashboard {
  auth_methods: number;
  registration_features: number;
  platform_roles: number;
  onboarding_steps: number;
  sync_domains: number;
  security_controls: number;
  analytics_funnels: number;
  landing_pages: number;
}

export function buildIdentityDashboard(): IdentityDashboard {
  return {
    auth_methods: AUTH_METHODS.length,
    registration_features: REGISTRATION_FEATURES.length,
    platform_roles: PLATFORM_ROLES.length,
    onboarding_steps: ONBOARDING_STEPS.length,
    sync_domains: SYNC_DOMAINS.length,
    security_controls: SECURITY_CONTROLS.length,
    analytics_funnels: 5,
    landing_pages: LANDING_PAGES.length,
  };
}

export const LIVE_ROUTES = {
  login: '/login',
  register: '/register',
  forgotPassword: '/forgot-password',
  onboarding: '/onboarding',
  amazonConnect: '/amazon/connect',
} as const;
